<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationAPI from 'dashboard/api/inbox/conversation';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const isLoading = ref(false);
const result = ref(null);

// Reset state when conversation changes
watch(
  () => props.conversationId,
  () => {
    result.value = null;
    isLoading.value = false;
  }
);

const buttonLabel = computed(() => {
  if (isLoading.value) return t('CONVERSATION_SIDEBAR.GROUP_CONTACTS.LOADING');
  return t('CONVERSATION_SIDEBAR.GROUP_CONTACTS.BUTTON');
});

const pullContacts = async () => {
  if (isLoading.value) return;
  isLoading.value = true;
  result.value = null;

  try {
    const response = await ConversationAPI.pullGroupContacts(
      props.conversationId
    );
    const data = response.data;
    result.value = data;
    useAlert(
      t('CONVERSATION_SIDEBAR.GROUP_CONTACTS.SUCCESS', {
        count: data.contacts_count,
      })
    );
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION_SIDEBAR.GROUP_CONTACTS.ERROR');
    useAlert(message);
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-2 p-2">
    <p class="mb-0 text-sm text-n-slate-11">
      {{ $t('CONVERSATION_SIDEBAR.GROUP_CONTACTS.DESCRIPTION') }}
    </p>
    <woot-button
      size="small"
      variant="smooth"
      color-scheme="primary"
      :is-loading="isLoading"
      @click="pullContacts"
    >
      {{ buttonLabel }}
    </woot-button>
    <div
      v-if="result && result.success"
      class="flex items-center gap-2 p-2 text-sm rounded-lg bg-g-50 text-g-800"
    >
      <fluent-icon icon="checkmark-circle" size="16" />
      <span>
        {{ result.contact_list_name }} ({{
          $t('CONVERSATION_SIDEBAR.GROUP_CONTACTS.CONTACTS_COUNT', {
            count: result.contacts_count,
          })
        }})
      </span>
    </div>
  </div>
</template>
